import { axiosRubyClient } from '@/util/axios/axiosClient';

// The badge/certificate catalog — what a client can be required to have and
// what a worker can be granted. Distinct from `../Certificates/api.ts`, which
// is worker-uploaded certificate PHOTO verification.
export type CertificateTypeLabel = 'BADGE' | 'CERTIFICATE';

export interface CertificateUsage {
  workers: number;
  customers: number;
  upcoming_jobs: number;
}

export interface Certificate {
  id: number;
  name: string;
  display_name_en: string | null;
  display_name_es: string | null;
  description_en: string | null;
  description_es: string | null;
  certificate_type: CertificateTypeLabel;
  image_key: string | null;
  image_url: string | null;
  gravy_trained: boolean | null;
  skill_id: number | null;
  deleted: boolean;
  agency_id: number;
  tenant_id: number;
  usage: CertificateUsage;
  created_at: string | null;
  updated_at: string | null;
}

export interface Pagination {
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface ListCertificatesResponse {
  success: boolean;
  data: Certificate[];
  pagination?: Pagination;
}

export interface SingleCertificateResponse {
  success: boolean;
  data: Certificate;
}

export interface ListParams {
  certificate_type?: CertificateTypeLabel | '';
  search?: string;
  include_deleted?: boolean;
  page?: number;
  per_page?: number;
}

export interface CertificateInput {
  name: string;
  display_name_en?: string;
  display_name_es?: string;
  description_en?: string;
  description_es?: string;
  certificate_type?: number;
  image_key?: string | null;
}

export const listCertificates = async (
  params: ListParams = {},
): Promise<ListCertificatesResponse> => {
  const response = await axiosRubyClient.get('/api/admin/certificates', {
    params,
  });
  return response.data;
};

export const createCertificate = async (
  input: CertificateInput,
): Promise<SingleCertificateResponse> => {
  const response = await axiosRubyClient.post('/api/admin/certificates', input);
  return response.data;
};

export const updateCertificate = async (
  id: number,
  input: Partial<CertificateInput>,
): Promise<SingleCertificateResponse> => {
  const response = await axiosRubyClient.patch(
    `/api/admin/certificates/${id}`,
    input,
  );
  return response.data;
};

// `detach` also removes the badge from clients and upcoming jobs. Without it
// the API refuses (409) while anything still requires the badge — a deleted
// badge keeps gating those jobs while disappearing from the picker.
export const deleteCertificate = async (
  id: number,
  detach = false,
): Promise<SingleCertificateResponse> => {
  const response = await axiosRubyClient.delete(
    `/api/admin/certificates/${id}`,
    { params: detach ? { detach: true } : {} },
  );
  return response.data;
};

// Axios rejects on 4xx, so the server's `errors` array lives on the error.
export const errorMessageFrom = (err: any, fallback: string): string => {
  const errors = err?.response?.data?.errors;
  if (Array.isArray(errors) && errors.length > 0) return errors.join(', ');
  return err?.message || fallback;
};

export const isConflict = (err: any): boolean => err?.response?.status === 409;
